import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import {
  Floor,
  FloorDoorState,
  InspectionCase,
  InspectionPressureSystemInStairwellsCompatibilityCheck,
  PressureSystemType,
  StairsRoom
} from '@authority-labs/api-interfaces';

@Component({
  selector: 'authority-labs-pressure-system-in-stairwells-compatibility-check',
  templateUrl:
    './pressure-system-in-stairwells-compatibility-check.component.html',
  styleUrls: [
    './pressure-system-in-stairwells-compatibility-check.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PressureSystemInStairwellsCompatibilityCheckComponent
  implements OnInit
{
  @Input() data: InspectionPressureSystemInStairwellsCompatibilityCheck;
  @Input() readonly: boolean;
  @Input() printPreview: boolean;
  @Output() dataChange: EventEmitter<void> = new EventEmitter();
  @ViewChildren(CdkVirtualScrollViewport)
  viewPortList: QueryList<CdkVirtualScrollViewport>;

  inspectionType = PressureSystemType.Stairway;
  numOfStairsRooms = 0;
  numOfFloors = 0;
  floorStartsAt = 0;
  caseTitlesMap: Map<string, InspectionCase[]> = new Map();

  doorStates = Object.values(FloorDoorState);
  pressureSystemTypes = Object.values(PressureSystemType);

  inverseOfTranslation(ind: number): number {
    if (!this.viewPortList || this.viewPortList.length === 0) {
      return 0;
    }
    if (
      !this.viewPortList.get(ind) ||
      !this.viewPortList.get(ind)['_renderedContentOffset']
    ) {
      return 0;
    }
    const offset = this.viewPortList.get(ind)['_renderedContentOffset'];
    return -offset;
  }

  get itemSize(): number {
    return 33;
  }

  get tableHeight(): number {
    return Math.min(
      400,
      100 + this.data?.stairsRooms[0]?.closedDoorsFloors?.length * this.itemSize
    );
  }

  constructor() {}

  ngOnInit(): void {
    this.setCaseTitlesMap();

    if (this.printPreview && !this.data.stairsRooms?.length) {
      this.numOfStairsRooms = 2;
      this.numOfFloors = 10;
      this.createTables();
    }
  }

  onDataChange(): void {
    this.setCaseTitlesMap();
    this.dataChange.emit();
  }

  toggleSprinklersTitle(
    pressureSystemType: PressureSystemType,
    withSprinklers: boolean
  ): string {
    const prefix =
      pressureSystemType === PressureSystemType.Stairway
        ? 'חדר מדרגות'
        : 'מסדרון מילוט';
    return withSprinklers ? `${prefix} עם מתזים` : `${prefix} בלי מתזים`;
  }

  isParentShown(parentSection: string, section: string): boolean {
    const parents = this.caseTitlesMap
      .get(parentSection)
      .filter((sc) => (section + '.').startsWith(sc.section + '.'));
    return parents.every((p) => p.isApplicable || p.section === section);
  }

  getParent(parentSection: string, section: string): InspectionCase[] {
    const parents = this.caseTitlesMap
      .get(parentSection)
      .filter(
        (sc) =>
          (section + '.').startsWith(sc.section + '.') && section !== sc.section
      );
    return parents;
  }

  margin(parentSection: string, section: string): string {
    const parents = this.getParent(parentSection, section);

    const margin = `${parents.length * 35}px`;
    // parent.length - 1 >= 1
    //   ? `${(parents.length - 1) * 35}px`
    //   : '0';

    return margin;
  }

  getStairsName(type: PressureSystemType, index: number) {
    return type === PressureSystemType.Stairway
      ? `חדר מדרגות מספר ${index + 1}`
      : `לחץ במסדרון מילוט מספר ${index + 1}`;
  }

  toggleWithSprinklers(): void {
    this.data.stairsRooms.forEach((sr) => {
      sr.closedDoorsFloors.forEach((floor) => {
        this.setDifferentialPressure(sr.withSprinklers, floor, false);
        this.setDoorOpeningPower(floor);
        this.setAirFlowSpeed(floor);
        this.setConclusion(floor);
      });
      sr.openDoorsFloors.forEach((floor) => {
        this.setDifferentialPressure(sr.withSprinklers, floor, true);
        this.setDoorOpeningPower(floor);
        this.setAirFlowSpeed(floor);
        this.setConclusion(floor);
      });
    });
    this.onDataChange();
  }

  doorToggle(withSprinklers: boolean, floor: Floor): void {
    this.setDifferentialPressure(withSprinklers, floor, true);
    this.setDoorOpeningPower(floor);
    this.setAirFlowSpeed(floor);
    this.setConclusion(floor);
    this.onDataChange();
  }

  onValueChange(floor: Floor): void {
    this.setConclusion(floor);
    this.onDataChange();
  }

  createTables() {
    if (this.numOfStairsRooms > 0 && this.numOfFloors) {
      this.data.stairsRooms = [];

      for (let i = 0; i < this.numOfStairsRooms; i++) {
        const closedDoorsFloors = Array(this.numOfFloors)
          .fill({})
          .map(
            (f, ind) =>
              new Floor({
                floorNumber: this.generateFloorNumber(ind),
                doorState: FloorDoorState.Closed
              })
          );
        const openDoorsFloors = Array(this.numOfFloors)
          .fill({})
          .map(
            (f, ind) =>
              new Floor({
                floorNumber: this.generateFloorNumber(ind),
                doorState: FloorDoorState.Closed
              })
          );

        const s = new StairsRoom({
          type: this.inspectionType,
          closedDoorsStairsName: this.getStairsName(this.inspectionType, i),
          openDoorsStairsName: this.getStairsName(this.inspectionType, i),
          closedDoorsFloors,
          openDoorsFloors
        });
        this.data.stairsRooms.push(s);
      }
    } else {
      this.data.stairsRooms = [];
    }

    this.toggleWithSprinklers();
  }

  isOpenDoor(doorState: FloorDoorState): boolean {
    return doorState !== FloorDoorState.Closed;
  }

  isCorridor(type: PressureSystemType): boolean {
    return type === PressureSystemType.Corridor;
  }

  isPassed(s: StairsRoom): boolean {
    return [s.closedDoorsFloors, s.openDoorsFloors]
      .flat()
      .every((d) => d.isPassed);
  }

  private generateFloorNumber(ind: number): string {
    return this.inspectionType === PressureSystemType.Stairway
      ? String(this.floorStartsAt + ind)
      : String(ind + 1);
  }

  private setDifferentialPressure(
    withSprinklers: boolean,
    floor: Floor,
    openDoorsTable: boolean
  ): void {
    if (withSprinklers) {
      floor.differentialPressure.min = openDoorsTable ? 0.063 : 0.125;
    } else {
      floor.differentialPressure.min = openDoorsTable ? 0.125 : 0.25;
    }
    if (this.isOpenDoor(floor.doorState)) {
      floor.differentialPressure.min = null;
      floor.differentialPressure.messured = null;
    }
  }

  private setDoorOpeningPower(floor: Floor): void {
    if (this.isOpenDoor(floor.doorState)) {
      floor.doorOpeningPower.max = null;
      floor.doorOpeningPower.messured = null;
    } else {
      floor.doorOpeningPower.max = 13.6;
    }
  }

  private setAirFlowSpeed(floor: Floor): void {
    if (this.isOpenDoor(floor.doorState)) {
      floor.airFlowSpeed.min = 1.0;
    } else {
      floor.airFlowSpeed.messured = null;
      floor.airFlowSpeed.min = null;
    }
  }

  private setCaseTitlesMap() {
    this.data.cases.forEach((c) => {
      this.caseTitlesMap.set(
        c.section,
        c.subCases.filter((sc) => sc.isTitle)
      );
    });
  }

  private setConclusion(floor: Floor): void {
    floor.isPassed =
      floor.doorOpeningPower.messured <= floor.doorOpeningPower.max &&
      floor.airFlowSpeed.messured >= floor.airFlowSpeed.min &&
      floor.differentialPressure.messured >= floor.differentialPressure.min;
  }
}
